// src/pages/admin/StorehouseList.js
import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Unstable_Grid2";
import { Button } from "@mui/material";
import { Breadcrumb } from "../../../components";
import { CardHeader } from "../../../components/cards";
import { Box } from "../../../components/elements";
import PageLayout from "../../../layouts/PageLayout";
import { DataGrid } from "@mui/x-data-grid";
import { Modal, ModalClose, Sheet, Typography } from "@mui/joy";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";

export default function StorehouseList() {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  const [storehouses, setStorehouses] = useState([]);
  const [callApi, setCallApi] = useState(false);
  const [warning, setWarning] = useState({ text: "", type: "" });
  const [selectedId, setSelectedId] = useState(null);
  const [openWarning, setOpenWarning] = useState(false);

  // Fetch all storehouses
  const fetchStorehouses = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BASE_URL}common/storehouses`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStorehouses(res.data);
    } catch (e) {
      alert("Session expired");
      localStorage.clear();
      navigate("/login");
    }
  };

  // Toggle active/inactive
  const changeStatus = async (id) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BASE_URL}common/storehouses/${id}`,
        { active: !storehouses.find(s => s._id === id).active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCallApi(prev => !prev);
    } catch (e) {
      alert(e.response?.data?.message || "Failed to change status");
    }
  };

  // Delete storehouse
  const deleteStorehouse = async (id) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_BASE_URL}common/storehouses/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCallApi(prev => !prev);
    } catch (e) {
      alert(e.response?.data?.message || "Failed to delete storehouse");
    }
  };

  useEffect(() => {
    fetchStorehouses();
  }, [callApi]);

  // Prepare columns for DataGrid
  const columns = [
    { field: "s_no", headerName: "S.no", width: 80 },
    { field: "code", headerName: "Code", width: 120 },
    { field: "name", headerName: "Name", width: 250 },
    { field: "active", headerName: "Active", width: 100 },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div style={{ display: "flex", gap: "0.5rem" }}>
          {/* Edit */}
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={() => navigate(`/edit-storehouse/${params.row.id}`)}
          >
            <i className="fas fa-edit" />
          </Button>

          {/* Toggle Active/Inactive */}
          <Button
            size="small"
            variant="contained"
            color={params.row.active === "Yes" ? "success" : "error"}
            onClick={() => {
              setSelectedId(params.row.id);
              setWarning({
                text: `Change status to ${params.row.active === "Yes" ? "Inactive" : "Active"}?`,
                type: "status",
              });
              setOpenWarning(true);
            }}
          >
            {params.row.active}
          </Button>

          {/* Delete */}
          <Button
            size="small"
            variant="contained"
            color="error"
            onClick={() => {
              setSelectedId(params.row.id);
              setWarning({ text: "Delete this storehouse?", type: "delete" });
              setOpenWarning(true);
            }}
          >
            <i className="fas fa-trash" />
          </Button>
        </div>
      )
    },
  ];

  // Prepare rows for DataGrid
  const rows = storehouses.map((s, idx) => ({
    id: s._id,
    s_no: idx + 1,
    code: s.code,
    name: s.name || "—",
    active: s.active ? "Yes" : "No",
  }));

  return (
    <PageLayout>
      <Grid container spacing={2}>
        <Grid
          size={{ xs: 12 }}
          className="d-flex justify-content-between align-items-center"
        >
          <Breadcrumb title="Storehouse List" />
          <Link to="/add-storehouse">
            <Button variant="contained" color="primary">Add Storehouse</Button>
          </Link>
        </Grid>

        <Grid size={{ xs: 12 }} className="mt-3">
          <Box className="mc-card">
            <CardHeader title="Storehouse List" />
            <div style={{ height: 600, width: '100%', marginTop: "1rem" }}>
              <DataGrid
                rows={rows}
                columns={columns}
                initialState={{
                  pagination: { paginationModel: { pageSize: 25 } },
                }}
                pageSizeOptions={[25, 50, 100]}
                disableRowSelectionOnClick
              />
            </div>
          </Box>
        </Grid>

        {/* Confirmation Modal */}
        <Modal
          aria-labelledby="modal-title"
          aria-describedby="modal-desc"
          open={openWarning}
          onClose={() => setOpenWarning(false)}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: "1000",
          }}
        >
          <Sheet
            variant="outlined"
            sx={{
              width: "30%",
              borderRadius: "md",
              p: 3,
              boxShadow: "lg",
              bgcolor: "#fff",
            }}
          >
            <ModalClose
              variant="outlined"
              sx={{
                top: "calc(-1/4 * var(--IconButton-size))",
                right: "calc(-1/4 * var(--IconButton-size))",
                boxShadow: "0 2px 12px 0 rgba(0 0 0 / 0.2)",
                borderRadius: "50%",
                bgcolor: "#eee",
              }}
            />
            <Typography
              id="basic-modal-dialog-title"
              component="h2"
              level="inherit"
              fontSize="1.25em"
              mb={5}
            >
              {warning.text}
            </Typography>

            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                marginTop: "2rem",
              }}
            >
              <Button
                variant="contained"
                color="success"
                onClick={(e) => {
                  e.preventDefault();
                  if (warning.type === "status") {
                    changeStatus(selectedId);
                  } else {
                    deleteStorehouse(selectedId);
                  }
                  setOpenWarning(false);
                }}
                style={{
                  width: "8rem",
                  fontSize: "0.8rem",
                  marginRight: "0.5rem",
                }}
              >
                Yes
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => setOpenWarning(false)}
                style={{
                  width: "8rem",
                  fontSize: "0.8rem",
                  marginRight: "0.5rem",
                }}
              >
                No
              </Button>
            </div>
          </Sheet>
        </Modal>
      </Grid>
    </PageLayout>
  );
}
